import { useEffect, useState } from 'react';
import { reportApi } from '../utils/api';
import { Wallet, Loader, Palmtree, Download } from 'lucide-react';
import { Button } from './ui/button';

const fmtDays = (n) => {
  const v = Math.round((n || 0) * 10) / 10;
  return v % 1 === 0 ? `${v}` : v.toFixed(1);
};

export const ManagerTeamBalances = () => {
  const [data, setData] = useState({ employees: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    reportApi.teamBalances()
      .then((res) => setData(res.data))
      .catch((e) => { console.error('team-balances error:', e); setData({ employees: [] }); })
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await reportApi.exportTeamBalances();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `saldos_equipo_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-team-balances">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
          <Wallet className="h-4 w-4 text-brand" />
          Saldos del equipo
          <span className="text-xs font-normal text-muted-foreground">(consulta · {data.year})</span>
        </h2>
        {data.employees.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            data-testid="btn-export-team-balances"
            className="gap-2 text-xs"
          >
            {exporting ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Exportar Excel
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-5 w-5 animate-spin text-brand" />
        </div>
      ) : data.employees.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-6">Sin miembros en el equipo.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="team-balances-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-2 font-medium">Empleado</th>
                <th className="py-2 pr-2 font-medium">Total</th>
                <th className="py-2 pr-2 font-medium">Usados</th>
                <th className="py-2 pr-2 font-medium">Pendiente aprobar</th>
                <th className="py-2 pr-2 font-medium">Disponibles</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp) => (
                <tr key={emp.user_id} data-testid={`balance-row-${emp.user_id}`} className="border-b border-border/60 last:border-0">
                  <td className="py-2 pr-2 font-medium truncate max-w-[200px]">{emp.user_name}</td>
                  <td className="py-2 pr-2 text-muted-foreground">{fmtDays(emp.vacation_total)}d</td>
                  <td className="py-2 pr-2 text-muted-foreground">{fmtDays(emp.vacation_used)}d</td>
                  <td className="py-2 pr-2">
                    {emp.vacation_pending > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">{fmtDays(emp.vacation_pending)}d</span>
                    ) : (
                      <span className="text-muted-foreground">0d</span>
                    )}
                  </td>
                  <td className="py-2 pr-2">
                    <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-semibold">
                      <Palmtree className="h-3 w-3" />
                      {fmtDays(emp.vacation_available)}d
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
